import bulkSessionHelper from '@/Helpers/BulkSession';
import { type FilingCabinet, type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { setAllowedToClose } from '@/Scripts/FilingCabinetLocks';
import {
    createContext,
    useContext,
    useEffect, useMemo, useState, type Dispatch,
    type PropsWithChildren,
    type SetStateAction,
} from 'react';
import { useNavigate, type NavigateFunction } from 'react-router';
import CloseLockedFilesDialog from '../../Components/CloseLockedFilesDialog/CloseLockedFilesDialog';
import useFilingCabinetBackend from '../../Hooks/UseFilingCabinetBackend';
import * as filingCabinetLocks from '../../Scripts/FilingCabinetLocks';
import checkROW2Link from './ROW2Link';

export interface FilingCabinetContextData extends FilingCabinetContextDataState, FilingCabinetContextDataActions {}

interface FilingCabinetContextDataState {
    filingCabinet: FilingCabinet;
    closeWarning: {
        show: boolean;
        single: boolean;
    };
}

interface FilingCabinetContextDataActions {
    clearFilingCabinet: (ignoreLocks?: boolean) => Promise<void>;
    generateNewID: () => number;
    getWithID: (id: number) => FilingCabinetItem | null;
    removeActive: (ignoreLocks?: boolean) => Promise<void>;
    removeFromFilingCabinet: (item: FilingCabinetItem, ignoreLocks?: boolean) => Promise<void>;
    add: (item: FilingCabinetItem) => void;
    save: (item: FilingCabinetItem) => void;
    loadByPath: (path: string) => FilingCabinetItem | null;
    openExisting: (item: FilingCabinetItem | null) => Promise<void>;
    setNoActive: () => void;
    getLockedFiles: () => FilingCabinetItem[];
    getActiveFcKey: () => number | null;
}

export const FilingCabinetContextActions = (
    filingCabinet: FilingCabinet,
    setFilingCabinet: Dispatch<SetStateAction<FilingCabinet>>,
    setCloseWarning: Dispatch<SetStateAction<{ item: FilingCabinetItem | null; show: boolean; single: boolean }>>,
    navigate: NavigateFunction,
): FilingCabinetContextDataActions => {
    const constructPath = ({
        ID,
        path,
    }: FilingCabinetItem): string => {
        // Filing Cabinet paths can potentially have query parameters (mainly ROW2), so when we add fc search param we need to make sure it is added properly
        const queryStringStart = path.indexOf('?');
        const hasQueryParameters = queryStringStart !== -1 && path.includes('=', queryStringStart);
        if (hasQueryParameters) {
            return `${path}&fc=${ID}`;
        }
        return `${path}?fc=${ID}`;
    };

    const open = async (item: FilingCabinetItem | null): Promise<void> => {
        if (window.onNavigateForRazor) {
            await window.onNavigateForRazor();
            window.onNavigateForRazor = null;
        }

        setActive(item);

        if (item) {
            const path = constructPath(item);
            navigate(path);
        }
        else {
            navigate('/');
        }
    };

    const setNoActive = (): void => {
        setFilingCabinet((prev) => {
            return {
                files: prev.files.map((f) => ({ ...f, active: false })),
            };
        });
    };

    const setActive = (item: FilingCabinetItem | null): void => {
        setFilingCabinet((prev) => {
            const activeID = item?.ID;

            return {
                files: prev.files.map((f) => ({
                    ...f,
                    active: f.ID === activeID,
                })),
            };
        });
    };

    const isActive = (item: FilingCabinetItem): boolean => {
        return filingCabinet.files.find((f) => item.ID === f.ID)?.active ?? false;
    };

    const getParentItem = (item: FilingCabinetItem): FilingCabinetItem | null => {
        const currentItemIndex: number | null = filingCabinet.files.findIndex((i) => i.ID === item.ID);

        if (currentItemIndex === -1) {
            return null;
        }

        if (currentItemIndex > 0) {
            return filingCabinet.files[currentItemIndex - 1];
        }

        return filingCabinet.files[currentItemIndex + 1];
    };

    const clearFilingCabinet = async (ignoreLocks?: boolean): Promise<void> => {
        if (window.onNavigateForRazor) {
            await window.onNavigateForRazor();
            window.onNavigateForRazor = null;
        }

        if (!ignoreLocks) {
            if (!filingCabinetLocks.isAllowedToClose() && filingCabinetLocks.areFilesLocked()) {
                setCloseWarning({ item: null, show: true, single: false });
                return;
            }
            setAllowedToClose(false);
        }

        filingCabinetLocks.removeAll();

        setFilingCabinet((_) => ({
            files: [],
        }));
        navigate('/');
    };

    const generateNewID = (): number => {
        const currentMaxID = filingCabinet?.files.reduce((max, f) => f.ID > max ? f.ID : max, 0) ?? 0;
        return currentMaxID + 1;
    };

    const getWithID = (id: number): FilingCabinetItem | null => {
        const matchingFile = filingCabinet?.files.find((f) => f.ID === id);

        return matchingFile ?? null;
    };

    const removeActive = async (ignoreLocks?: boolean): Promise<void> => {
        const active = filingCabinet.files.find((f) => f.active);

        if (!active) {
            return;
        }

        await removeFromFilingCabinet(active, ignoreLocks);
    };

    window.filingCabinetRemoveActive = removeActive; // NOTE: To be able to do "Save and Close" functionality

    const removeFromFilingCabinet = async (item: FilingCabinetItem, ignoreLocks?: boolean): Promise<void> => {
        if (!ignoreLocks && !filingCabinetLocks.isAllowedToClose() && filingCabinetLocks.getLocks(item.ID).length) {
            setCloseWarning({ item, show: true, single: true });
            return;
        }
        setAllowedToClose(false);

        const active = isActive(item);
        const parentToNavigateTo = getParentItem(item);

        filingCabinetLocks.removeLocks(item.ID);

        setFilingCabinet((prev) => {
            const files = prev?.files.filter((x) => x.ID !== item.ID);
            return { ...prev, files };
        });

        // Was the active file
        if (active) {
            await open(parentToNavigateTo);
        }
    };

    const add = (item: FilingCabinetItem): void => {
        _updateFilingCabinet(item, false);
        setActive(item);
    };

    const save = (item: FilingCabinetItem): void => {
        _updateFilingCabinet(item, true);
    };

    const _updateFilingCabinet = (item: FilingCabinetItem, isSave: boolean): void => {
        const { ID } = item;
        setFilingCabinet(function (item: FilingCabinetItem, isSave: boolean, prev: any) {
            // NOTE: If we come from the save function we only want to save the filing cabinet state if the item is also the active one.
            //       There are cases where the save is triggered because there is some residual html changes when closing the RazorView and we don't want to save in that case.
            //       Otherwise the deletion of the item will not work as expected
            if (isSave && !prev.files.find((x: FilingCabinetItem) => x.ID === item.ID)?.active) {
                return prev;
            }

            const indexOfItem = prev.files.findIndex((f: FilingCabinetItem) => f.ID === ID);
            const isNewEntry = indexOfItem === -1;

            const newFilingCabinet = { ...prev };
            if (isNewEntry) {
                const currActiveIdx = prev.files.findIndex((f: FilingCabinetItem) => f.active);
                newFilingCabinet.files.splice(currActiveIdx + 1, 0, item);
            }
            else {
                newFilingCabinet.files[indexOfItem] = item;
            }

            return newFilingCabinet;
        }.bind(null, item, isSave));
    };

    const loadByPath = (path: string): FilingCabinetItem | null => {
        const [targetPath, targetQuery] = path.split('?');
        return filingCabinet.files.find((f) => {
            const [filePath, fileQuery] = f.path.split('?');
            if (filePath !== targetPath) {
                return false;
            }
            if (targetQuery === undefined) {
                return true;
            }
            return fileQuery === targetQuery;
        }) ?? null;
    };

    const getLockedFiles = (): FilingCabinetItem[] => {
        return filingCabinet.files.filter((f) => f.isLocked);
    };

    const getActiveFcKey = (): number | null => {
        const activeItem = filingCabinet.files.find((f) => f.active);
        return activeItem?.ID ?? null;
    };

    return {
        clearFilingCabinet,
        generateNewID,
        getWithID,
        removeActive,
        removeFromFilingCabinet,
        add,
        save,
        loadByPath,
        openExisting: open,
        setNoActive,
        getLockedFiles,
        getActiveFcKey,
    };
};

export const FilingCabinetContext = createContext<FilingCabinetContextData | null>(null);

export const FilingCabinetProvider = ({ children }: PropsWithChildren): React.ReactElement => {
    const navigate = useNavigate();
    const [filingCabinetBackend, setFilingCabinetBackend] = useFilingCabinetBackend();
    const [closeWarning, setCloseWarning] = useState<{ item: FilingCabinetItem | null; show: boolean; single: boolean }>({ item: null, show: false, single: false });

    const actions = useMemo(() => {
        return FilingCabinetContextActions(filingCabinetBackend, setFilingCabinetBackend, setCloseWarning, navigate);
    }, [filingCabinetBackend, setFilingCabinetBackend, setCloseWarning, navigate]);
    const filingCabinetContextValue = useMemo(() => ({
        filingCabinet: filingCabinetBackend,
        closeWarning,
        ...actions,
    }), [closeWarning, filingCabinetBackend, actions]);

    // Overload useFilingCabinet so that we can handle page changes manually
    window.useFilingCabinet = async (path: string, replaceFC = false, replaceHistory = false, ignoreLocks?: boolean): Promise<void> => {
        if (window.onNavigateForRazor) {
            await window.onNavigateForRazor();
            window.onNavigateForRazor = null;
        }
        if (replaceFC) {
            await actions.removeActive(ignoreLocks);
        }

        const hasLinked = checkROW2Link(path);
        if (hasLinked) {
            return;
        }

        navigate(path, { replace: replaceHistory });
    };

    const handleKeyUp = (event: any): void => {
        if (event.code === 'Escape') {
            actions.removeActive().catch((err) => console.error('Could not remove the active file from filing cabinet', err));
        }
    };

    useEffect(() => {
        window.addEventListener('keyup', handleKeyUp);
        return () => window.removeEventListener('keyup', handleKeyUp);
    }, [actions]);

    const onCloseOk = (item: FilingCabinetItem | null): void => {
        if (item?.path) {
            bulkSessionHelper.clearBulkSessions(item.path);
        }
        if (closeWarning.single && item != null) {
            actions.removeFromFilingCabinet(item, true).catch((err) => console.error('Could not remove from filing cabinet', err));
        }
        else {
            actions.clearFilingCabinet().catch((err) => console.error('Could not clear filing cabinet', err));
        }
        setCloseWarning({ ...closeWarning, show: false });
    };

    return (
        <FilingCabinetContext.Provider value={filingCabinetContextValue}>
            <CloseLockedFilesDialog
                item={closeWarning.item}
                isOpen={closeWarning.show}
                single={closeWarning.single}
                okCb={onCloseOk}
                cancelCb={() => {
                    setCloseWarning({ ...closeWarning, show: false });
                }}
            />
            {children}
        </FilingCabinetContext.Provider>
    );
};

export const useFilingCabinetContext = (): FilingCabinetContextData => useContext(FilingCabinetContext)!;
